import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Link from '@/components/shared/Link';
import Collapse from '@/components/shared/Collapse';
import DefaultLayout from '@/components/layout/DefaultLayout';
import { CommonPageProps, ImageShape, NonUndefined, Product, ProductState, VideoShape } from '@/types';
import { getCommonPageProps } from '@/api/common';
import { tp } from '@/typograf';
import { getProduct } from '@/api/product';
import { toCurrency } from '@/utils/to-currency';
import { useRouter } from 'next/router';
import CollapseArrowSVG from '@/svg/collapse-arrow.svg?url';
import { useEffect, useRef, useState } from 'react';
import { useMediaQueryDeviceState } from '@/atoms/media-query-device';
import { CATEGORIES_IDS, CategoryId, CATEGORIES_TEXT_DICT, CATEGORIES_SLUGS } from '@/api/categories';
import { useSetSelectedServiceValueFormState } from '@/atoms/form/selected-service-value-form-state';
import { FEEDBACK_POPUP_NAME } from '@/components/general/FeedbackPopup/FeedbackPopup';
import { useSetSelectedItemsState } from '@/atoms/form/selected-items-state';
import { useRoubleToDollarCourseState } from '@/atoms/rouble-to-dollar-course';
import { useEuroToDollarCourseState } from '@/atoms/euro-to-dollar-course';
import PlaySVG from '@/svg/play.svg?url';
import proofPreviewImage from '../../../../public/img/proof-preview-2.png';
import dynamic from 'next/dynamic';
import { useSetOpenedPopupsState } from '@/atoms/opened-popups';
import Button from '@/components/shared/Button';
import Image from 'next/image';
import ShareButton from '@/components/shared/ShareButton';
import { useCookies } from 'react-cookie';
import { RECENTS_COOKIES_NAME } from '@/pages/viewed';
import { addUniqueItemToArray } from '@/utils/arrays/add-unique-item-to-array';
import { YD_NEW_ID } from '@/metrics/ym';
import VideoPopup, { VIDEO_POPUP_NAME } from '@/components/general/VideoPopup/VideoPopup';
import { useTypograf } from '@/hooks/use-typograf';
import { useSetFeedbackPopupDataNameState } from '@/components/general/FeedbackPopup/use-feedback-popup-data-name-atom';
import WhatsappSVG from '@/svg/whatsapp.svg?url';
import TelegramSVG from '@/svg/telegram.svg?url';
import { useMotionValueEvent, useScroll } from 'framer-motion';
import { useSetCallbackVisibleState } from '@/atoms/callback-visible';
import RecommendationsList from '@/components/general/RecommendationsList';
import { stateDict } from '@/dicts';
import { getServices, Service } from '@/api/services';
import { getRecProducts } from '@/api/rec-products';
import ServiceDetailCardsList from '@/components/shared/ServiceDetailCardsList';
import { useNavProductDataState } from '@/atoms/product-nav-state';
import ProductInfoSection from '@/components/general/ProductInfoSection';
import ButtonBack from '@/components/shared/ButtonBack';

const FavoriteButton = dynamic(() => import('@/components/shared/Card/FavoriteButton'), { ssr: false });
const CompareButton = dynamic(() => import('@/components/shared/Card/CompareButton'), { ssr: false });
const DownloadVideoButton = dynamic(() => import('@/components/shared/DownloadVideoButton'), { ssr: false });
const ProductSlider = dynamic(() => import('@/components/shared/ProductSlider'), { ssr: false });

const searchHelp: Record<CategoryId, string> = {
    1: 'Часов',
    2: 'Сумки',
    3: 'Украшения',
    4: 'Аксессуара',
    5: 'Chrome Hearts',
};

const questionsMap = [
    {
        title: 'Как работает услуга "под заказ"?',
        text: `<ul>
  <li>
    <span class="step-title">Обсуждение параметров.</span>
    <div class="step-text">Мы детально уточняем все пожелания по изделию.</div>
  </li>
  <li>
    <span class="step-title">Согласование сроков.</span>
    <div class="step-text">Обычно срок поставки составляет от 4 до 7 дней, в зависимости от доступности позиции.</div>
  </li>
  <li>
    <span class="step-title">Внесение предоплаты.</span>
    <div class="step-text">
      Для подтверждения заказа менеджер выезжает в любую удобную для вас локацию.<br />
      Сумма предоплаты составляет 5–15% от стоимости изделия — в зависимости от его категории. Это служит гарантией выкупа и закрепляет позицию за вами.
    </div>
  </li>
  <li>
    <span class="step-title">Оформление.</span>
    <div class="step-text">
      По запросу клиента предоставляется документальное подтверждение получения предоплаты с указанием всех условий сделки — включая характеристики изделия и сроки поставки.
    </div>
  </li>
  <li>
    <span class="step-title">Проверка подлинности.</span>
    <div class="step-text">
      До окончательной оплаты вы можете провести независимую экспертизу в любом авторитетном сервисе.
    </div>
  </li>
</ul>
`,
    },
    {
        title: 'Какие гарантии подлинности вы предоставляете?',
        text: `<div class="step-text">
    Каждые часы сопровождаются полным комплектом оригинальных документов — аналогичным тому, что вы получаете в официальном бутике бренда.
  </div>
  <div class="step-text">
    Перед продажей все изделия проходят детальную проверку подлинности, включая сверку с официальными базами, чтобы исключить любые риски, связанные с происхождением.
  </div>
  <div class="step-text">
    По вашему желанию вы можете провести дополнительную экспертизу в любой авторитетной компании — мы полностью открыты и уверены в безупречности каждого изделия.
  </div>`,
    },
    {
        title: 'Предоставляете ли вы услугу подбора инвестиционных изделий?',
        text: `<div class="step-text">
    Да, мы предлагаем индивидуальный подбор инвестиционно привлекательных экземпляров.
  </div>
  <div class="step-text">
    В своей работе опираемся на аналитику ведущих аукционных домов и многолетнюю экспертизу на рынке. Такие изделия — редкость, и доступ к ним требует особых связей.
  </div>
  <div class="step-text">
    Нас поддерживает обширная сеть коллекционеров. В отдельных случаях возможен также подбор редких камней напрямую с месторождений — минуя цепочку посредников.
  </div>`,
    },
    {
        title: 'Не могу определиться с размером. Вы можете помочь?',
        text: ` <div class="step-text">
    Разумеется. Мы располагаем актуальными таблицами размеров всех представленных брендов и поможем точно подобрать идеальный вариант, учитывая посадку конкретной модели и ваши предпочтения.
  </div>`,
    },
    {
        title: 'Хочу продать, сдать в trade-in или на комиссию изделие. Как проходит оценка?',
        text: ` <div class="step-text">
    Оценка проводится на основе актуальной стоимости изделия на вторичном рынке.
  </div>
  <div class="step-text">
    Мы предлагаем одни из самых конкурентных условий, благодаря прямому сотрудничеству с международными аукционными домами, частными коллекционерами и сертифицированными дилерами по всему миру.
  </div>`,
    },
];

const DetailPage = ({
    h1,
    price,
    reference,
    diameter,
    isStandardForm,
    accessories,
    color,
    type,
    params,
    insert,
    collection,
    mechanism,
    bracelet,
    powerReserve,
    dialColor,
    waterResistance,
    dialMaterial,
    dialStyle,
    calibre,
    glass,
    gems,
    gemsInBezel,
    gemsInBracelet,
    gemsInCase,
    typesGems,
    materials,
    brand,
    genders,
    options,
    productImages,
    productVideo,
    productVideos,
    additionalCharacteristics,
    accordions,
    recommendations,
    productId,
    categoryId,
    available,
    socnets,
    state,
    box,
    documents,
    year,
    services,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const setServiceValueFormState = useSetSelectedServiceValueFormState();
    const setFeedbackPopupDataName = useSetFeedbackPopupDataNameState();

    const { openPopup } = useSetOpenedPopupsState();
    const setSelectedItems = useSetSelectedItemsState();
    const setCallbackVisible = useSetCallbackVisibleState();

    const [course] = useRoubleToDollarCourseState();
    const [euroCourse] = useEuroToDollarCourseState();

    const currentCategoryCookiesName = `${RECENTS_COOKIES_NAME}-${categoryId}`;
    const [cookies, setCookie] = useCookies(
        Object.keys(CATEGORIES_SLUGS).map((item) => `${RECENTS_COOKIES_NAME}-${item}`),
    );
    const viewed = cookies[RECENTS_COOKIES_NAME]?.includes(productId);

    const containerRef = useRef<HTMLDivElement>(null);
    const productCenterRef = useRef<HTMLDivElement>(null);
    const productCenterHeight = productCenterRef.current?.offsetHeight || 0;

    const [mediaQueryDevice] = useMediaQueryDeviceState();

    const [activeVideo, setActiveVideo] = useState<VideoShape | null>(null);

    const scrollToSection = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, id: string) => {
        event.preventDefault();
        const section = document.querySelector(id);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const tpH1 = useTypograf(h1);
    const tpOriginalityText = useTypograf(
        'Каждое изделие проходят всестороннюю экспертизу на подлинность и качество. Для вашего комфорта мы подготовили подробное видео, показывающее весь процесс проверки поэтапно.',
    );

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ['start start', 'end end'],
    });

    useMotionValueEvent(scrollYProgress, 'change', (val) => {
        setCallbackVisible(val > 0.125);
    });
    const [, setNavProductData] = useNavProductDataState();

    useEffect(() => {
        setNavProductData({
            price,
            title: tpH1,
            available: available,
        });
    }, [price, tpH1, available, setNavProductData]);

    useEffect(() => {
        if (!viewed) {
            if (cookies[currentCategoryCookiesName]) {
                setCookie(
                    currentCategoryCookiesName,
                    addUniqueItemToArray(cookies[currentCategoryCookiesName], Number(productId)),
                );
            } else {
                setCookie(currentCategoryCookiesName, [Number(productId)]);
            }
        }
    }, [viewed, productId, setCookie, currentCategoryCookiesName]);

    const brandName = brand.name?.trim() ?? '';
    const fullName = h1.trim();
    const modelName = fullName.replace(new RegExp(`^${brandName}\\s*`, 'i'), '').trim();

    return (
        <DefaultLayout ref={containerRef}>
            <div className="product-layout">
                <div className="product-center--wrapper">
                    <div className="product-center" ref={productCenterRef}>
                        <ButtonBack
                            fallbackHref={`/catalog?category=${categoryId}&sort=published_at&order=desc`}
                            className="product-back-button-touch back-button-clickable"
                        />
                        <div className="product-buttons">
                            <FavoriteButton
                                variant="product"
                                productId={Number(productId)}
                                className="product-button"
                            />
                            <CompareButton
                                variant="card"
                                productId={Number(productId)}
                                categoryId={categoryId}
                                className="product-button"
                            />

                            <DownloadVideoButton activeVideo={activeVideo} />
                            <ShareButton
                                className="product-button btn-share"
                                url={typeof window !== 'undefined' ? window.location.href : ''}
                                title={h1}
                            />
                        </div>
                        <ProductSlider
                            images={productImages}
                            video={productVideo}
                            videos={productVideos}
                            onDownloadVideo={setActiveVideo}
                        />
                    </div>
                    {mediaQueryDevice !== 'desktop' && (
                        <div
                            className="product-characteristics-wrapper"
                            style={{ top: `${productCenterHeight + 30}px` }}
                        >
                            <Button
                                className="product-characteristics-btn product-lower-price-button"
                                geometryVariant="mustache"
                                icon={<Image src="/img/back-reverse.svg" alt="" unoptimized fill />}
                                onClick={(e) => scrollToSection(e, '#product-right')}
                            >
                                Посмотреть характеристики
                            </Button>
                        </div>
                    )}
                </div>

                <div className="product-columns">
                    <div className="product-left">
                        <div className="product-left__top">
                            <ButtonBack
                                fallbackHref={`/catalog?category=${categoryId}`}
                                className="product-back-button-desktop back-button-clickable"
                            />
                        </div>

                        <div className="product-left__center">
                            <div className="product-name-wrapper">
                                <Button
                                    tag={Link}
                                    href={`/catalog?category=${categoryId}&brands[]=${brand.id}&sort=published_at&order=desc`}
                                    icon={<Image src="/img/back-reverse.svg" alt="" unoptimized fill />}
                                    className="product-all-available h2 button-arrow-reverse"
                                    geometryVariant="arrow"
                                >
                                    {brandName}
                                </Button>

                                <h1 className="h2 product-name">{modelName}</h1>

                                {reference && <span className="product-ref text-xs">{`REF\u00A0${reference}`}</span>}
                            </div>

                            {price !== 0 && (
                                <div className="product-info">
                                    <div className="product-info__left">
                                        <div className="product-info__top-price">
                                            <div className="product-info__text text-xs">{tp('Цена в Москве')}</div>
                                            {course && course !== 0 && (
                                                <>
                                                    <div className="price--rouble text-l">
                                                        {toCurrency(price * course, 'RUB')}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className="product-info__price-wrapper">
                                            <div className="product-price--dollar text-l">
                                                {toCurrency(price, 'USD')}
                                            </div>
                                            {euroCourse && euroCourse !== 0 && (
                                                <div className="price--rouble text-l">
                                                    {toCurrency(price * euroCourse, 'EUR')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="product-info__right">
                                        <div className="product-info__text text-l">
                                            {price !== 0
                                                ? tp(
                                                      'СТОИМОСТЬ УКАЗАНА БЕЗ УЧЕТА СОПУТСТВУЮЩИХ РАСХОДОВ. ОНИ  ОБЫЧНО СОСТАВЛЯЮТ ОТ 2% ДО 15% ОТ ИТОГОВОЙ СУММЫ. МЫ РАБОТАЕМ ПО ВСЕМУ МИРУ.',
                                                  )
                                                : tp(
                                                      'Цена может варьироваться, как в большую, так и в меньшую сторону в зависимости от страны доставки. Для уточнения всех деталей свяжитесь с нашим менеджером. Мы работаем по всему миру.',
                                                  )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {price !== 0 && (
                                <div className="product-lower-price">
                                    <Button
                                        geometryVariant="mustache"
                                        className="product-lower-price-button"
                                        onClick={() => {
                                            setServiceValueFormState('Нашли дешевле?');
                                            setSelectedItems([h1]);
                                            setFeedbackPopupDataName('cheaper');
                                            openPopup(FEEDBACK_POPUP_NAME);
                                        }}
                                    >
                                        Нашли дешевле?
                                    </Button>

                                    <div className="product-lower-price__text text-s">
                                        Нашли дешевле? Нажми, чтобы получить лучшее ценовое предложение
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="product-cta product-cta--detail">
                            <div className="product-cta__bottom-btns">
                                <Button
                                    variant="bordered-light"
                                    className="product-trade-in-button text-l"
                                    onClick={() => {
                                        setServiceValueFormState('под заказ');
                                        setFeedbackPopupDataName('order');
                                        openPopup(FEEDBACK_POPUP_NAME);
                                    }}
                                >
                                    {`Помощь в поиске ${searchHelp[categoryId]}`}
                                </Button>
                                <Button
                                    variant="bordered-light"
                                    className="product-trade-in-button text-l"
                                    onClick={() => {
                                        setServiceValueFormState('Trade-in');
                                        setSelectedItems([h1]);
                                        openPopup(FEEDBACK_POPUP_NAME);
                                        setFeedbackPopupDataName('trade-in');
                                        window.ym?.(YD_NEW_ID, 'reachGoal', 'click_trade_in_button');
                                    }}
                                >
                                    Trade-in
                                </Button>
                                <Button
                                    variant="bordered-light"
                                    className="product-trade-in-button text-l"
                                    onClick={() => {
                                        setServiceValueFormState('Sell');
                                        setSelectedItems([h1]);
                                        openPopup(FEEDBACK_POPUP_NAME);
                                        setFeedbackPopupDataName('sell');
                                    }}
                                >
                                    Продать
                                </Button>
                            </div>
                        </div>

                        <div className="product-left__available">
                            <div className="product-available__top">
                                <div
                                    className="product-available-text text-xs"
                                    dangerouslySetInnerHTML={{
                                        __html: available ? 'в&nbsp;наличии в&nbsp;Москве' : 'под заказ',
                                    }}
                                />
                                <Button
                                    tag={Link}
                                    href={`/catalog?category=${categoryId}&brands[]=${brand.id}&sort=published_at&order=desc`}
                                    icon={<Image src="/img/back-reverse.svg" alt="" unoptimized fill />}
                                    className="product-all-available text-l button-arrow-reverse"
                                    geometryVariant="arrow"
                                >
                                    Все {CATEGORIES_TEXT_DICT[categoryId]}
                                    {` ${brand.name}`}
                                </Button>
                            </div>
                            <div className="product-available__bottom">
                                <span className="product-available__subtext text-xs">
                                    {tp('возможна доставка в любую страну')}
                                </span>
                            </div>
                        </div>

                        {(state || box || documents || year) && (
                            <div className="product-blocks">
                                <ul className="product-blocks__list">
                                    {state && (
                                        <li className="product-block-item text-xs">
                                            <span className="product-block-item__title">Состояние</span>
                                            <span>{stateDict[state as ProductState]}</span>
                                        </li>
                                    )}
                                    {box && (
                                        <li className="product-block-item text-xs">
                                            <span className="product-block-item__title">Коробка</span>
                                            <span>{box ? 'Есть' : 'Нет'}</span>
                                        </li>
                                    )}
                                    {documents && (
                                        <li className="product-block-item text-xs">
                                            <span className="product-block-item__title">Документы</span>
                                            <span>{documents ? 'Есть' : 'Нет'}</span>
                                        </li>
                                    )}
                                    {year && (
                                        <li className="product-block-item text-xs">
                                            <span className="product-block-item__title">Год</span>
                                            <span>{year}</span>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}

                        {mediaQueryDevice !== 'desktop' && (
                            <ProductInfoSection
                                brand={brand}
                                categoryId={categoryId}
                                reference={reference}
                                diameter={diameter}
                                isStandardForm={isStandardForm}
                                accessories={accessories}
                                color={color}
                                type={type}
                                params={params}
                                insert={insert}
                                collection={collection}
                                mechanism={mechanism}
                                bracelet={bracelet}
                                powerReserve={powerReserve}
                                dialColor={dialColor}
                                waterResistance={waterResistance}
                                dialMaterial={dialMaterial}
                                dialStyle={dialStyle}
                                calibre={calibre}
                                glass={glass}
                                materials={materials}
                                genders={genders}
                                options={options}
                                additionalCharacteristics={additionalCharacteristics}
                                accordions={accordions}
                                gems={gems}
                                gemsInBezel={gemsInBezel}
                                gemsInBracelet={gemsInBracelet}
                                gemsInCase={gemsInCase}
                                typesGems={typesGems}
                            />
                        )}

                        {questionsMap && questionsMap.length > 0 && (
                            <div className="product-questions">
                                <div className="h2 product-questions__title">Часто задаваемые вопросы</div>
                                {questionsMap.map((question) => (
                                    <Collapse className="product-collapse" collapsed key={question.title}>
                                        <Collapse.Toggler className="product-collapse-toggler">
                                            <div className="product-collapse-header text-xs">{question.title}</div>
                                            <div className="product-collapse-toggler__icon"></div>
                                        </Collapse.Toggler>
                                        <Collapse.Content>
                                            <div className="product-collapse-content">
                                                <div
                                                    className="product-collapse-text text-s"
                                                    dangerouslySetInnerHTML={{
                                                        __html: question.text,
                                                    }}
                                                />
                                            </div>
                                        </Collapse.Content>
                                    </Collapse>
                                ))}
                            </div>
                        )}

                        {socnets.length > 0 && (
                            <div className="product-socs product-socs--first">
                                <div className="h2 product-socs__title">Остались вопросы?</div>
                                <div className="product-socs__btns">
                                    {socnets.map((soc) => (
                                        <Button
                                            key={soc.name}
                                            href={soc.link}
                                            tag={Link}
                                            target="_blank"
                                            rel="noreferrer"
                                            icon={
                                                soc.name.toLowerCase() === 'whatsapp' ? (
                                                    //  eslint-disable-next-line @next/next/no-img-element
                                                    <img
                                                        src={WhatsappSVG}
                                                        alt=""
                                                        width={20}
                                                        height={20}
                                                        className="img-fluid"
                                                        decoding="async"
                                                    />
                                                ) : (
                                                    soc.name.toLowerCase() === 'telegram' && (
                                                        //  eslint-disable-next-line @next/next/no-img-element
                                                        <img
                                                            src={TelegramSVG}
                                                            alt=""
                                                            width={16}
                                                            height={16}
                                                            className="img-fluid"
                                                            decoding="async"
                                                        />
                                                    )
                                                )
                                            }
                                            iconReverse
                                            variant="bordered-light"
                                        >
                                            {soc.name}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {socnets.length > 0 && (
                            <div className="product-socs">
                                <div className="h2 product-socs__title">
                                    {tp('Следите за новыми поступлениями и скидками')}
                                </div>
                                <div className="product-socs__btns">
                                    {socnets.map((soc) => {
                                        return (
                                            soc.name.toLowerCase() === 'telegram' && (
                                                <Button
                                                    key={soc.name}
                                                    href={soc.link}
                                                    tag={Link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    icon={
                                                        //  eslint-disable-next-line @next/next/no-img-element
                                                        <img
                                                            src={TelegramSVG}
                                                            alt=""
                                                            width={16}
                                                            height={16}
                                                            className="img-fluid"
                                                            decoding="async"
                                                        />
                                                    }
                                                    iconReverse
                                                    variant="bordered-light"
                                                >
                                                    {tp('подписаться на telegram - канал')}
                                                </Button>
                                            )
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="product-proof">
                            <button
                                className="product-proof__image"
                                onClick={() => {
                                    openPopup(VIDEO_POPUP_NAME);
                                }}
                            >
                                <Image
                                    src={proofPreviewImage}
                                    width={117}
                                    height={117}
                                    alt=""
                                    className="product-proof__image-el"
                                />
                                <div className="product-proof__image-icon">
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src={PlaySVG}
                                        alt=""
                                        width={10}
                                        height={10}
                                        className="img-fluid"
                                        decoding="async"
                                    />
                                </div>
                            </button>

                            <div className="product-proof__right">
                                <div className="product-proof__title text-xs">{tp('гарантия оригинальности')}</div>
                                <div
                                    className="product-proof__description text-s"
                                    dangerouslySetInnerHTML={{ __html: tpOriginalityText }}
                                />
                            </div>
                        </div>

                        <div className="product-text-block">
                            <div className="product-text-block__container">
                                <div className="m-text-xs product-text-block__title">Гарантия</div>
                                <div
                                    className="text-s product-text-block__text"
                                    dangerouslySetInnerHTML={{
                                        __html: `
                                    На подержанные часы и украшения действует гарантия не менее 12 месяцев.
                                    <br />
                                    <br />
                                    Новые изделия сопровождаются международной гарантией сроком от 2 лет — в зависимости от политики конкретного бренда.
                                    <br />
                                    <br />
                                    Каждое изделие проходит очную экспертизу на подлинность и полное соответствие стандартам качества.
                                    <br />
                                    <br />
                                    Бутик ROTORMINE также предоставляет 1 год дополнительной гарантии на случайзаводского брака.
                                    <br />
                                    <br />
                                    Вы можете быть уверены: если изделие окажется неисправным или не будет соответствовать заявленным характеристикам, мы предложим замену, профессиональный ремонт или полный возврат средств, включая все понесённые расходы.
                                    `,
                                    }}
                                ></div>
                            </div>
                        </div>

                        <ServiceDetailCardsList services={services} />
                    </div>

                    {mediaQueryDevice === 'desktop' && (
                        <ProductInfoSection
                            brand={brand}
                            categoryId={categoryId}
                            reference={reference}
                            diameter={diameter}
                            isStandardForm={isStandardForm}
                            accessories={accessories}
                            color={color}
                            type={type}
                            params={params}
                            insert={insert}
                            collection={collection}
                            mechanism={mechanism}
                            bracelet={bracelet}
                            powerReserve={powerReserve}
                            dialColor={dialColor}
                            waterResistance={waterResistance}
                            dialMaterial={dialMaterial}
                            dialStyle={dialStyle}
                            calibre={calibre}
                            glass={glass}
                            materials={materials}
                            genders={genders}
                            options={options}
                            additionalCharacteristics={additionalCharacteristics}
                            accordions={accordions}
                            gems={gems}
                            gemsInBezel={gemsInBezel}
                            gemsInBracelet={gemsInBracelet}
                            gemsInCase={gemsInCase}
                            typesGems={typesGems}
                        />
                    )}
                </div>
            </div>

            {recommendations.length > 0 && <RecommendationsList recommendations={recommendations} />}

            <VideoPopup />
        </DefaultLayout>
    );
};

export default DetailPage;

type ProductPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        categoryId: CategoryId;
        price: number;
        available: boolean;
        reference: string | null;
        diameter: string | null;
        isStandardForm: boolean;
        accessories: string | null;
        color: string | null;
        type: string | null;
        params: string | null;
        insert: string | null;
        collection: string | null;
        mechanism: string | null;
        bracelet: string | null;
        powerReserve: string | null;
        dialColor: string | null;
        waterResistance: string | null;
        dialMaterial: string | null;
        dialStyle: string | null;
        calibre: string | null;
        glass: string | null;
        gems: boolean | null;
        gemsInBezel: boolean | null;
        gemsInBracelet: boolean | null;
        gemsInCase: boolean | null;
        typesGems: string | null;
        state: number;
        box: boolean;
        documents: boolean;
        year: string | null;
        materials: {
            id: number;
            slug: string;
            name: string;
        }[];
        brand: {
            id: number;
            slug: string;
            name: string;
            description: string;
        };
        genders: {
            id: number;
            slug: string;
            name: string;
        }[];
        options: {
            id: number;
            name: string;
        }[];
        productImages: ImageShape[];
        productVideo: VideoShape;
        productVideos: VideoShape[];
        additionalCharacteristics: {
            name: string;
            value: string;
        }[];
        accordions: {
            name: string;
            text: string;
        }[];
        recommendations: Omit<Product, 'brand_name' | 'favorite'>[];
        services: Service[];
        productId: string;
    }
>;

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async ({ query }) => {
    const productId = query.id as string;
    if (!productId) {
        return { notFound: true };
    }

    const productInfo = await getProduct(productId)
        .then((res) => res)
        .catch((err) => err);

    if (productInfo && productInfo.status === 'error') {
        if (productInfo.status === 404) {
            return { notFound: true };
        }
        throw productInfo.repsonse.data.errors[0];
    }

    const [commonPageProps, recomendations, services] = await Promise.all([
        getCommonPageProps(),
        getRecProducts({
            params: { product_id: Number(productId), limit: 5 },
        }),
        getServices(),
    ]);

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'product-page',
            meta: {
                ...commonPageProps.meta,
                ...productInfo?.data.meta,
            },
            h1: tp(productInfo?.data.name || ''),
            price: productInfo?.data?.price || 0,
            reference: productInfo?.data?.reference || null,
            diameter: productInfo?.data?.diameter || null,
            isStandardForm: productInfo?.data?.is_standard_form ?? true,
            accessories: productInfo?.data?.accessories || null,
            color: productInfo?.data?.color || null,
            type: productInfo?.data?.type || null,
            params: productInfo?.data?.params || null,
            insert: productInfo?.data?.insert || null,
            collection: productInfo?.data?.collection || null,
            mechanism: productInfo?.data?.mechanism || null,
            bracelet: productInfo?.data?.bracelet || null,
            powerReserve: productInfo?.data?.power_reserve || null,
            dialColor: productInfo?.data?.dial_color || null,
            waterResistance: productInfo?.data?.water_resistance || null,
            dialMaterial: productInfo?.data?.dial_material || null,
            dialStyle: productInfo?.data?.dial_style || null,
            calibre: productInfo?.data?.calibre || null,
            glass: productInfo?.data?.glass || null,
            gems: productInfo?.data?.gems ?? false,
            gemsInBezel: productInfo?.data?.gems_in_bezel ?? false,
            gemsInBracelet: productInfo?.data?.gems_in_bracelet ?? false,
            gemsInCase: productInfo?.data?.gems_in_case ?? false,
            typesGems: productInfo?.data?.types_gems ?? null,
            available: productInfo?.data?.available,
            state: productInfo?.data?.state || 0,
            box: productInfo?.data?.box || false,
            documents: productInfo?.data?.documents || false,
            year: productInfo?.data?.year || null,
            materials:
                productInfo?.data?.materials.map((item: { id: number; slug: string; name: string }) => {
                    return {
                        ...item,
                        name: tp(item.name),
                    };
                }) || [],
            brand: productInfo?.data?.brand || {
                id: 0,
                slug: '',
                name: '',
                description: '',
            },
            genders:
                productInfo?.data?.genders.map((item: { id: number; slug: string; name: string }) => {
                    return {
                        ...item,
                        name: tp(item.name),
                    };
                }) || [],
            options:
                productInfo?.data?.options.map((item: { id: number; name: string }) => {
                    return {
                        ...item,
                        name: tp(item.name),
                    };
                }) || [],
            productImages: productInfo?.data?.images || [],
            productVideo: productInfo?.data.video ?? [],
            productVideos: productInfo?.data.videos ?? [],
            additionalCharacteristics:
                productInfo?.data?.additional_characteristics.map((item: { name: string; value: string }) => {
                    return {
                        name: tp(item.name),
                        value: tp(item.value),
                    };
                }) || [],
            accordions: (productInfo?.data?.accordions ?? [])
                .map((item: { name: string; text: string }) => {
                    return {
                        name: tp(item.name),
                        text: typeof item.text === 'string' ? tp(item.text) : item.text,
                    };
                })
                .filter((item: any) => !!item.text),
            recommendations: (recomendations.data?.items || []).map((item) => ({
                id: item.id,
                slug: item.slug,
                name: item.name,
                price: item.price,
                material: item.material ?? '',
                state: item.state,
                images: item.images,
                reference: item.reference,
                diameter: (item.diameter ?? '').toString(),
                size: item.size ?? '',
                accessories: item.accessories ?? '',
                productColor: item.color ?? '',
                type: item.type ?? '',
                insert: item.insert ?? '',
                category_id: productInfo?.data?.category_id as CategoryId,
                available: item.available,
            })),
            productId,
            categoryId: productInfo?.data?.category_id || CATEGORIES_IDS['watch'],
            services: services?.data ?? [],
        } satisfies ProductPageProps,
    };
};
